import React, { useState, useMemo } from "react";
import PromptEditor from "../PromptEditor";
import AddWebsitePromptSection from "../AddWebsitePromptSection";
import {
  useDeleteStagingPromptMutation,
  useDeleteProdPromptMutation,
} from "../../../../../app/api/console/scanTemplateSyncApiSlice";

const PromptsTab = ({ template, environment, templateId, websiteMap, websitePromptIds }) => {
  const [previewWebsiteId, setPreviewWebsiteId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [deleteStagingPrompt, { isLoading: isDeletingStaging }] = useDeleteStagingPromptMutation();
  const [deleteProdPrompt, { isLoading: isDeletingProd }] = useDeleteProdPromptMutation();
  const isDeleting = environment === "staging" ? isDeletingStaging : isDeletingProd;

  // Not memoised on purpose: `template.website_prompts` is a stable prop reference and the
  // `[]` fallback only applies when the template has none; wrapping it in useMemo would
  // change its identity semantics for the `mergedText` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const websitePrompts = template.website_prompts || [];
  const baseText = template.base_prompt?.current_prompt_text || "";

  const mergedText = useMemo(() => {
    if (!previewWebsiteId) return baseText;
    const wp = websitePrompts.find((p) => p.website_id === previewWebsiteId);
    if (!wp?.current_prompt_text) return baseText;
    return `${baseText}\n\n## Website-Specific Instructions\n\n${wp.current_prompt_text}`;
  }, [baseText, previewWebsiteId, websitePrompts]);

  const handleDeletePrompt = async () => {
    if (!deleteTarget) return;
    const deleteFn = environment === "staging" ? deleteStagingPrompt : deleteProdPrompt;
    await deleteFn({ promptId: deleteTarget.prompt_id });
    setDeleteTarget(null);
  };

  return (
    <div className="flex gap-6">
      {/* Left: prompt editors */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Prompts</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {template.base_prompt ? "1 base prompt" : "No base prompt"}
              {websitePrompts.length > 0 && ` + ${websitePrompts.length} website-specific`}
            </p>
          </div>
        </div>

        {/* Base prompt */}
        {template.base_prompt && (
          <PromptEditor
            prompt={template.base_prompt}
            environment={environment}
            templateId={templateId}
            onDelete={() => setDeleteTarget({ prompt_id: template.base_prompt.prompt_id, label: "Base Prompt" })}
          />
        )}

        {/* Website prompts */}
        {websitePrompts.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              Website-Specific Prompts
            </h4>
            <div className="space-y-3">
              {websitePrompts.map((prompt) => (
                <PromptEditor
                  key={prompt.prompt_id}
                  prompt={prompt}
                  environment={environment}
                  templateId={templateId}
                  websiteName={websiteMap[prompt.website_id]}
                  onDelete={() => setDeleteTarget({ prompt_id: prompt.prompt_id, label: websiteMap[prompt.website_id] || prompt.website_id })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Add prompt */}
        <AddWebsitePromptSection
          templateId={templateId}
          environment={environment}
          hasBasePrompt={!!template.base_prompt}
          websitePromptIds={websitePromptIds}
        />
      </div>

      {/* Right: merged preview (always visible if there are prompts) */}
      {baseText && (
        <div className="w-80 flex-shrink-0">
          <div className="sticky top-0 bg-white rounded-xl border-2 border-green-200 overflow-hidden">
            <div className="bg-green-50 px-4 py-3 border-b border-green-200">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <h4 className="text-sm font-semibold text-green-900">AI Receives</h4>
              </div>
              <p className="text-xs text-green-700 mt-0.5">The actual prompt sent to the scan engine</p>
              {websitePrompts.length > 0 && (
                <select
                  value={previewWebsiteId}
                  onChange={(e) => setPreviewWebsiteId(e.target.value)}
                  className="mt-2 w-full px-2 py-1.5 border border-green-300 rounded-md text-xs bg-white focus:ring-2 focus:ring-green-200 focus:outline-none"
                >
                  <option value="">Base prompt only (generic)</option>
                  {websitePrompts.map((p) => (
                    <option key={p.website_id} value={p.website_id}>
                      Merged for: {websiteMap?.[p.website_id] || p.website_id}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="p-3">
              <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[60vh] overflow-y-auto text-gray-700">
                {mergedText.split("## Website-Specific Instructions").map((part, i) => (
                  <span key={i}>
                    {i > 0 && (
                      <span className="block my-2 py-1 px-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs font-sans font-semibold not-italic">
                        Website-Specific Instructions
                      </span>
                    )}
                    {part}
                  </span>
                ))}
              </pre>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-green-100">
                <span className="text-xs text-gray-400">{mergedText.length} chars total</span>
                {previewWebsiteId && (
                  <span className="text-xs text-amber-600 font-medium">
                    Includes {websiteMap?.[previewWebsiteId] || "website"} overrides
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl border border-border-subtle">
            <h3 className="text-body-lg font-semibold text-text-primary mb-2">Delete Prompt</h3>
            <p className="text-body-sm text-text-secondary mb-5">
              Are you sure you want to delete <strong>{deleteTarget.label}</strong>? All versions will be permanently removed. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-border-default rounded-xl text-body-sm text-text-secondary hover:bg-surface-neutral transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePrompt}
                disabled={isDeleting}
                className="px-4 py-2 bg-severity-high text-white rounded-xl text-body-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptsTab;
